import { Box, Stack, SvgIcon, useTheme } from '@mui/material';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect } from 'react';
import { expandIcon } from '../../config/icons';
import ControlButtons from './components/control-buttons';
import Slide from './components/slide';
import { Thumbnails } from './components/thumbnails';
import { StyledNavigationButton } from './styles';
import { ECarouselVariant } from './types';

const fullscreenButtonSx = {
  borderRadius: '8px',
  top: '15px',
  left: '15px',
  width: '40px',
  height: '40px',
};

type CarouselContentProps = {
  slides: Array<{ url: string; isVideo: boolean }>;
  mainImageUrl?: string;
  variant?: ECarouselVariant;
  withFullscreen?: boolean;
  thumbnailsButtons?: boolean;
  onFullScreenClick?: () => void;
  fullscreenOnImageClick?: boolean;
  onOpenFullscreen: () => void;
};

export function CarouselContent({
  slides,
  mainImageUrl,
  variant,
  withFullscreen,
  thumbnailsButtons,
  onFullScreenClick,
  fullscreenOnImageClick,
  onOpenFullscreen,
}: CarouselContentProps) {
  const direction = useTheme().direction;
  const isRTL = direction === 'rtl';

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
      containScroll: false,
      direction,
    },
    [
      Autoplay({
        // We set a generous delay for video slides and then when the video ends, we will switch automatically to the next slide. This is faster than calculating the video duration.
        // So far, we don't have videos longer than 5 minutes. If we do get some in the future, then we will need to increase the delay.
        delay: () => slides.map((slide) => (slide.isVideo ? 50000 : 5000)),
      }),
    ],
  );

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, slides]);

  return (
    <>
      <Box ref={emblaRef} height="100%">
        <Stack
          direction="row"
          height="100%"
          onClick={() => {
            if (fullscreenOnImageClick && onFullScreenClick) onFullScreenClick();
          }}
        >
          {slides.map((slide, index) => (
            <Slide
              key={`${slide.url}-${index}`}
              url={slide.url}
              isVideo={slide.isVideo}
              variant={variant}
              emblaApi={emblaApi}
              style={{
                marginLeft: '8px',
                marginRight: '8px',
              }}
            />
          ))}
        </Stack>

        {withFullscreen && (
          <StyledNavigationButton
            onClick={() => {
              if (onFullScreenClick) {
                onFullScreenClick();
              } else {
                onOpenFullscreen();
              }
            }}
            data-treedistestid="button-carousel-fullscreen"
            sx={fullscreenButtonSx}
          >
            <SvgIcon>{expandIcon}</SvgIcon>
          </StyledNavigationButton>
        )}

        {slides.length > 1 && thumbnailsButtons && (
          <ControlButtons
            isRTL={isRTL}
            variant={variant}
            onNext={() => emblaApi?.scrollNext()}
            onPrev={() => emblaApi?.scrollPrev()}
          />
        )}
      </Box>
      <Thumbnails
        slides={slides}
        mainImageUrl={mainImageUrl}
        emblaMainApi={emblaApi}
        variant={variant}
      />
    </>
  );
}
